export const CONSENT_STATUSES = ["draft", "active", "inactive", "archived"];

export const CONSENT_STATUS_META = {
  draft: { label: "Draft", color: "default" },
  active: { label: "Active", color: "success" },
  inactive: { label: "Inactive", color: "warning" },
  archived: { label: "Archived", color: "error" },
};

export const statusMeta = (status) =>
  CONSENT_STATUS_META[status] || { label: status || "—", color: "default" };

export const PREDEFINED_CONSENT_TYPES = [
  { code: "RCT", label: "RCT Consent" },
  { code: "TOOTH_EXTRACTION", label: "Tooth Extraction Consent" },
  { code: "SCALING", label: "Scaling Consent" },
  { code: "PERIO", label: "Perio Consent" },
  { code: "PEDO", label: "Pedo Consent" },
  { code: "ORTHO", label: "Ortho Consent" },
  { code: "IMPLANT", label: "Implant Consent" },
  { code: "ORAL_SURGERY", label: "Oral Surgery Consent" },
  { code: "REFERRAL", label: "Referral Consent" },
  { code: "DIAGNOSTIC", label: "Diagnostic Consent" },
  { code: "COST", label: "Cost Consent" },
  { code: "PATHOLOGY", label: "Pathology Consent" },
  {
    code: "HYPERSENSITIVITY_ANESTHESIA",
    label: "Hypersensitivity Anesthesia Consent",
  },
  { code: "GENERAL_ANESTHESIA", label: "General Anesthesia Consent" },
  { code: "LOCAL_ANESTHESIA", label: "Local Anesthesia Consent" },
  { code: "PHOTOGRAPHIC", label: "Photographic Consent" },
  { code: "COMPLAINT", label: "Complaint Form" },
  { code: "REFUSAL", label: "Refusal Form" },
  {
    code: "PROCEDURE",
    label: "Procedure Consent",
    displayName: "Procedure Consent Form",
    hindiName: "प्रक्रिया सहमति पत्र",
  },
  { code: "REVISED_ESTIMATE", label: "Revised Estimated Consent" },
  {
    code: "TREATMENT",
    label: "Treatment Consent",
    displayName: "Treatment Consent Form",
    hindiName: "उपचार सहमति पत्र",
  },
  {
    code: "DATA_SHARING",
    label: "Data Sharing Consent",
    displayName: "Patient Data Sharing Consent Form",
    hindiName: "रोगी डेटा साझा करने की सहमति पत्र",
  },
  {
    code: "RESEARCH",
    label: "Research Participation Consent",
    displayName: "Research Participation Consent Form",
    hindiName: "अनुसंधान में भागीदारी सहमति पत्र",
  },
];

const KNOWN_TYPES = new Set(PREDEFINED_CONSENT_TYPES.map((item) => item.code));

export const CUSTOM_TYPE = "__custom__";

export const typeOptions = (meta = {}) => {
  const predefined = meta.predefinedTypes?.length
    ? meta.predefinedTypes
    : PREDEFINED_CONSENT_TYPES;

  const byCode = new Map();
  for (const option of predefined) {
    if (option?.code && !byCode.has(option.code)) byCode.set(option.code, option);
  }
  for (const code of meta.customTypes || []) {
    if (code && !KNOWN_TYPES.has(code) && !byCode.has(code)) {
      byCode.set(code, { code, label: code, custom: true });
    }
  }

  return [
    ...byCode.values(),
    { code: CUSTOM_TYPE, label: "Custom consent format…" },
  ];
};

export const isPredefinedType = (code) => KNOWN_TYPES.has(code);

/** A code typed by hand: upper snake case, so it reads like the others. */
export const toTypeCode = (label) =>
  String(label || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);

export const typeLabelOf = (template = {}) =>
  template.typeLabel?.trim() ||
  PREDEFINED_CONSENT_TYPES.find((item) => item.code === template.type)?.label ||
  template.type ||
  "—";

export const versionLabel = (version) =>
  version ? `v${version}` : "—";

export const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${date.getFullYear()}`;
};

export const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return `${formatDate(value)} ${date
    .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    .toUpperCase()}`;
};

export const nextStatusFor = (template = {}) =>
  template.status === "active" ? "inactive" : "active";

export const canActivate = (template = {}) =>
  Boolean(template.publishedVersion);

export const activateBlockedReason = (template = {}) =>
  canActivate(template)
    ? ""
    : "Publish a version before activating this consent";

/** A published working version means the next design edit opens a new version. */
export const editsOpenNewVersion = (version = {}) =>
  version.status === "published";

export const versionStatusLabel = (version = {}) =>
  version.status === "published" ? "Published" : "Draft";
